import type { DrillDifficulty } from '@/types';

/**
 * 根据最近 N 次演练成绩推荐难度
 *
 * 规则：
 * - 最近 5 次平均分 >= 85 → 推荐升级（但 expert 不再升）
 * - 最近 5 次平均分 < 50 → 推荐降级（但 novice 不再降）
 * - 50-85 之间 → 维持当前难度
 * - 少于 3 次记录 → 不推荐（数据不足）
 *
 * `recentScores` 按时间正序排列，最后一个为最新一次的成绩。
 */
export interface DifficultyRecommendation {
  recommended: DrillDifficulty; // 推荐的难度
  current: DrillDifficulty; // 当前难度
  shouldChange: boolean; // 是否建议切换
  reason: string; // 推荐理由（中文）
  avgScore: number; // 最近 5 次平均分
  sampleSize: number; // 样本数
}

/** 难度中文名映射（UI 展示统一用这里，避免散落硬编码） */
export const DIFFICULTY_LABEL: Record<DrillDifficulty, string> = {
  novice: '新手',
  standard: '进阶',
  expert: '专家',
};

/** 难度升级顺序：novice → standard → expert */
const NEXT_UP: Record<DrillDifficulty, DrillDifficulty> = {
  novice: 'standard',
  standard: 'expert',
  expert: 'expert',
};

/** 难度降级顺序：expert → standard → novice */
const NEXT_DOWN: Record<DrillDifficulty, DrillDifficulty> = {
  expert: 'standard',
  standard: 'novice',
  novice: 'novice',
};

/** 计算平均分：保留一位小数，空数组返回 0 */
function average(scores: number[]): number {
  if (scores.length === 0) return 0;
  const sum = scores.reduce((acc, s) => acc + s, 0);
  return Math.round((sum / scores.length) * 10) / 10;
}

/**
 * 根据最近 N 次演练成绩推荐难度
 *
 * @param recentScores 最近 N 次的分数数组（按时间正序，最后一个是最新的）
 * @param currentDifficulty 当前难度
 */
export function recommendDifficulty(
  recentScores: number[],
  currentDifficulty: DrillDifficulty,
): DifficultyRecommendation {
  // 取最后 5 个（或更少）
  const sample = recentScores.slice(-5);
  const sampleSize = sample.length;
  const avgScore = average(sample);

  // 数据不足：样本数 < 3 不做推荐
  if (sampleSize < 3) {
    return {
      recommended: currentDifficulty,
      current: currentDifficulty,
      shouldChange: false,
      reason: '演练次数不足 3 次，暂不推荐',
      avgScore,
      sampleSize,
    };
  }

  // 表现优秀：avg >= 85 → 推荐升级（expert 不再升）
  if (avgScore >= 85) {
    if (currentDifficulty === 'expert') {
      return {
        recommended: 'expert',
        current: currentDifficulty,
        shouldChange: false,
        reason: '你的表现已经很出色，可以继续挑战专家模式',
        avgScore,
        sampleSize,
      };
    }
    const recommended = NEXT_UP[currentDifficulty];
    return {
      recommended,
      current: currentDifficulty,
      shouldChange: true,
      reason: `最近 ${sampleSize} 次平均 ${avgScore} 分，表现出色，建议升级到${DIFFICULTY_LABEL[recommended]}模式挑战更高难度`,
      avgScore,
      sampleSize,
    };
  }

  // 表现偏弱：avg < 50 → 推荐降级（novice 不再降）
  if (avgScore < 50) {
    if (currentDifficulty === 'novice') {
      return {
        recommended: 'novice',
        current: currentDifficulty,
        shouldChange: false,
        reason: '建议多练习新手模式，熟悉基本操作后再提升难度',
        avgScore,
        sampleSize,
      };
    }
    const recommended = NEXT_DOWN[currentDifficulty];
    return {
      recommended,
      current: currentDifficulty,
      shouldChange: true,
      reason: `最近 ${sampleSize} 次平均 ${avgScore} 分，当前难度有些吃力，建议回到${DIFFICULTY_LABEL[recommended]}模式巩固基础`,
      avgScore,
      sampleSize,
    };
  }

  // 50 <= avg < 85：维持当前难度
  return {
    recommended: currentDifficulty,
    current: currentDifficulty,
    shouldChange: false,
    reason: '当前难度适合你，继续保持',
    avgScore,
    sampleSize,
  };
}
